import axios, { isAxiosError } from 'axios';
import { AuthService } from '../services/AuthService';
import { store } from '../store/store';
import {
  addTechnicalError,
  type ITechnicalError
} from '../store/technicalError/technicalError';
import { getErrorData } from './getErrorData';
import { parseQueryParams } from './parseQueryParams';
import { suppressToast } from './suppressToast';
import { isAccessDeniedMessage } from './isAccessDeniedMessage';
import { isDeliveryNotFoundError } from './isDeliveryNotFoundError';
import { isPackingConfirmationError } from './isPackingConfirmationError';
import { isPackingRequestWithoutForce } from './isPackingRequestWithoutForce';
// TODO(refactor-delivery): dev-логгер delivery-запросов отключён (см. ниже). Импорт
// закомментирован; восстановить при необходимости отладки.
// import { createDeliveryDevLogger } from './createDeliveryDevLogger';

export const createApiInstance = (baseURL: string) => {
  const instance = axios.create({ baseURL });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (axios.isCancel(error)) {
        return await Promise.reject(error);
      }

      const originalRequest = error.config;

      if (
        isAxiosError(error) &&
        originalRequest !== undefined &&
        originalRequest !== null &&
        error.response?.status === 401 &&
        !((originalRequest.url as string | undefined) ?? '').includes(
          '/public/login/refresh'
        )
      ) {
        // eslint-disable-next-line no-console
        console.log('[API Interceptor] 401 ошибка, запуск refresh токена');

        try {
          const response = await AuthService.refreshAuthToken();

          if (response === undefined) {
            // eslint-disable-next-line no-console
            console.error(
              '[API Interceptor] Refresh вернул undefined, прерывание запроса'
            );
            return await Promise.reject(new Error('Не удалось обновить токен'));
          }

          // eslint-disable-next-line no-console
          console.log('[API Interceptor] Токен обновлен, повтор запроса');

          originalRequest.headers.Authorization = `Bearer ${response.authToken}`;
          return await instance.request(originalRequest);
        } catch (refreshError) {
          // eslint-disable-next-line no-console
          console.error('[API Interceptor] Ошибка при refresh:', refreshError);
          return await Promise.reject(refreshError);
        }
      }

      if (
        isAxiosError(error) &&
        originalRequest !== undefined &&
        originalRequest !== null
      ) {
        let endpoint = '';
        if (
          error.request !== null &&
          typeof error.request.responseURL === 'string' &&
          error.request.responseURL.length > 0
        ) {
          endpoint = error.request.responseURL;
        } else if (
          error.config !== undefined &&
          error.config !== null &&
          typeof error.config.url === 'string' &&
          error.config.url !== ''
        ) {
          endpoint = error.config.url;
        }

        const params = endpoint !== '' ? parseQueryParams(endpoint) : undefined;
        const raw = error.response?.data as unknown;
        // rawObj — безопасное приведение response.data к объекту (или null)
        const rawObj =
          raw != null && typeof raw === 'object' && !Array.isArray(raw)
            ? (raw as Record<string, unknown>)
            : null;
        // metaFromRaw — берем meta только если rawObj действительно объект
        const metaFromRaw =
          rawObj != null && typeof rawObj['meta'] === 'object'
            ? (rawObj['meta'] as Record<string, unknown>)
            : undefined;

        const errorData = getErrorData(error);
        const isNetworkError = error.response == null && error.request != null;
        const shouldSuppressToast = suppressToast(errorData.message);

        // Проверяем, нужно ли подавить тост для ошибки упаковки без force
        const isPackingWithoutForce = isPackingRequestWithoutForce(endpoint);
        const isSpecificPackingError = isPackingConfirmationError(
          error.response?.data
        );
        const shouldSuppressPackingToast =
          isPackingWithoutForce && isSpecificPackingError;

        // Нормализация "detail"
        let responseDetail: string | undefined;
        if (rawObj != null && typeof rawObj['detail'] === 'string') {
          responseDetail = (rawObj['detail'] as string).trim();
        } else if (typeof raw === 'string') {
          responseDetail = raw.trim();
        } else {
          responseDetail = undefined;
        }

        const isAccessDenied =
          error.response?.status === 403 &&
          isAccessDeniedMessage(responseDetail);

        // Текст для проверки берём из detail, а при его отсутствии — из message
        // (ожидаемая ошибка доставки приходит как { message, code }, без detail).
        const isExpectedDeliveryError = isDeliveryNotFoundError(
          responseDetail ?? errorData.message,
          error.response?.status,
          endpoint,
          errorData.code
        );

        const accessDeniedMessage =
          'У вас нет доступа к информации на этой странице.';

        // Сериализация data в допустимый тип для ITechnicalErrorResponse.data
        const responseDataSerialized: string | object | null | undefined =
          raw == null
            ? undefined
            : typeof raw === 'object'
              ? (raw as Record<string, unknown>)
              : String(raw);

        const serializableError: ITechnicalError = {
          id: Date.now(),
          message: isAccessDenied
            ? accessDeniedMessage
            : isNetworkError
              ? 'Network Error: Сервер не вернул ответ. Возможные причины: слишком большой файл, проблемы с сетью, ограничения прокси/серверов.'
              : errorData.message,
          request: {
            method: originalRequest.method,
            url: endpoint,
            params,
            data: originalRequest.data
          },
          response: {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: responseDataSerialized,
            meta: { ...(metaFromRaw ?? {}), accessDenied: isAccessDenied },
            message: error.message
          },
          networkError: isNetworkError,
          suppressToast: shouldSuppressToast || shouldSuppressPackingToast
        };

        if (!isExpectedDeliveryError) {
          store.dispatch(addTechnicalError(serializableError));
        }
      }

      return await Promise.reject(error);
    }
  );

  instance.interceptors.request.use(
    (config) => {
      const savedToken = AuthService.authToken;
      if (savedToken != null) {
        config.headers.Authorization = `Bearer ${savedToken}`;
      }
      return config;
    },
    async (error) => {
      return await Promise.reject(error);
    }
  );

  return instance;
};

export const api = createApiInstance(import.meta.env.VITE_API_URL + '/api/');

// TODO(refactor-delivery): dev-логгер delivery/address-запросов отключён по завершении
// переезда на основной бэкенд. Утилита createDeliveryDevLogger оставлена — включить при
// необходимости отладки delivery-эндпоинтов (см. RefusalDelivery.md).
// createDeliveryDevLogger(api);

// TODO(refactor-delivery): устаревший инстанс старого сервиса доставки. Delivery-вызовы
// переведены на основной api — инстанс больше не используется. Закомментирован вместе с
// VITE_DELIVERY_BACKEND_URL; оставлен для отката.
// export const deliveryApi = createApiInstance(
//   import.meta.env.VITE_DELIVERY_BACKEND_URL
// );
